import { useNavigate } from "react-router-dom";

type IPageRef = {
  pageRef?: React.MutableRefObject<HTMLDivElement | null>;
};

function Introduction({ pageRef }: IPageRef) {
  const navigate = useNavigate();
  return (
    <div className="mt-[25px]" ref={pageRef} title="Account Deletion">
      <div className="text-[#2a3342] text-[18px] xlsm:text-[17px] leading-[28px] font-[400] mt-[15px] tracking-normal space-y-6">
        {/* What Happens Section */}
        <section>
          <h2 className="font-[600] text-[20px] text-primary-main mb-3">
            What Happens When You Delete Your Shiga Account
          </h2>
          <p className="font-[400] text-[14px] mb-4">
            Users can now deactivate their accounts directly via{" "}
            <span className="font-[600]">Settings → Security</span>, eliminating
            the need to email deactivation requests to help@shiga.io.
          </p>
          <p className="mt-4 font-[600]">
            After confirming account deactivation, you will no longer be able
            to:
          </p>
          <ul className="list-disc pl-6 mt-2 space-y-2 font-[400] text-[14px] ">
            <li>Log in to your Shiga account</li>
            <li>
              Reuse the same email address or mobile number to create a new
              Shiga account
            </li>
            <li>
              Verify another account using the same KYC documents (if
              applicable)
            </li>
            <li>
              Access or reactivate any Subaccounts linked to your Main Account
            </li>
          </ul>
        </section>

        {/* How to Deactivate Section */}
        <section>
          <h2 className="font-[600] text-[20px] text-primary-main mb-3">
            How to Deactivate Your Shiga Account
          </h2>

          <div className="bg-[#F8FAFC] p-5 rounded-lg">
            <h3 className="font-[600] text-[18px] text-hbrand-500 mb-3">
              Method 1: When Logged In (Self-Service)
            </h3>
            <img
              src="/assets/deleteAcc.png"
              alt="contact-us"
              className="w-full h-[400px] xlsm:object-cover object-left"
            />
            <ol className="space-y-4 mt-4 pl-5 list-decimal">
              <li>
                <span className="font-[500]">
                  Navigate to Security Settings
                </span>
                <ul className="list-disc pl-5 mt-1 space-y-1 font-[400] text-[14px] ">
                  <li>Log in to your Shiga account</li>
                  <li>Click your profile icon (top-right corner)</li>
                  <li>
                    Select{" "}
                    <span className="font-[500]">
                      Settings → Account & Security
                    </span>
                  </li>
                </ul>
              </li>
              <li>
                <span className="font-[500]">Initiate Deactivation</span>
                <ul className="list-disc pl-5 mt-1 space-y-1 font-[400] text-[14px] ">
                  <li>
                    Under <span className="font-[500]">Account Activities</span>
                    , click{" "}
                    <span className="font-[500]">Deactivate Account</span>
                  </li>
                  <li>
                    Select your reason:
                    <ul className="list-circle pl-5 mt-1 space-y-1">
                      <li>Suspicious activity (for compromised accounts)</li>
                      <li>Discontinuing use (voluntary closure)</li>
                      <li>Other (describe in text box)</li>
                    </ul>
                  </li>
                </ul>
              </li>
              <li>
                <span className="font-[500]">Confirm Deactivation</span>
                <ul className="list-disc pl-5 mt-1 space-y-1 font-[400] text-[14px] ">
                  <li>Review consequences (see warnings below)</li>
                  <li>
                    Click <span className="font-[500]">Confirm</span> to
                    finalize
                  </li>
                </ul>
              </li>
            </ol>
          </div>

          <div className="bg-[#F8FAFC] p-5 rounded-lg mt-5">
            <h3 className="font-[600] text-[18px] text-hbrand-500 mb-3">
              Method 2: When Logged Out (Assisted Process)
            </h3>
            <img
              src="/assets/contactUs.png"
              alt="contact-us"
              className="w-full h-[400px] xlsm:object-cover object-right"
            />
            <ol className="space-y-4 mt-4 pl-5 list-decimal">
              <li>
                <span className="font-[500]">Start Deactivation Request</span>
                <ul className="list-disc pl-5 mt-1 space-y-1 font-[400] text-[14px] ">
                  <li>
                    {" "}
                    <p
                      onClick={() => {
                        navigate("/contact-support");
                      }}
                      className="cursor-pointer font-[500] text-hbrand-700 px-2 py-[1.5px] rounded-[4px]"
                    >
                      Visit Shiga Contact Page
                    </p>
                  </li>
                  <li>Write us a message and we'll get in touch via email</li>
                </ul>
              </li>
            </ol>
          </div>
        </section>

        {/* Immediate Effects Section */}
        <section>
          <h2 className="font-[600] text-[20px] text-primary-main mb-3">
            Immediate Effects
          </h2>
          <ul className="list-disc pl-6 space-y-2 font-[400] text-[14px] ">
            <li>Login access revoked permanently</li>
            <li>Registered email/phone cannot be reused</li>
            <li>KYC-verified documents are permanently linked</li>
          </ul>
        </section>

        {/* Security Recommendation */}
        <section className="bg-[#FFF8F5] p-4 rounded-lg border-l-4 border-hbrand-500">
          <h3 className="font-[600] text-[18px] text-[#2a3342] mb-2">
            Security Recommendation
          </h3>
          <ul className="list-disc pl-6 space-y-1 font-[400] text-[14px] ">
            <li>
              Choose <span className="font-[500]">Suspicious Activity</span>{" "}
              reason if account was compromised
            </li>
            <li>Clear remaining balances before deactivation</li>
          </ul>
        </section>

        {/* Support Link */}
        <p className="text-[16px]">
          Need help? Contact our support team at{" "}
          <p
            onClick={() => {
              navigate("/contact-support");
            }}
            className="cursor-pointer inline-block font-[600] text-hbrand-500"
          >
            Contact Support
          </p>
        </p>
      </div>
    </div>
  );
}

export default Introduction;
